import posthog from 'posthog-js';
import i18next from 'i18next';
import { User } from '../model/dto/userProfile/user';
import { Industry } from '../model/dto/userProfile/industry';
import { getConsentHintFromCookie, hasAnalyticsConsent, isConsentKnown } from './consent';

// anltcs forwards the client IP (geo data); hogproxy strips it
const CONSENTED_HOST = 'https://anltcs.laerdal.com';
const ANONYMOUS_HOST = 'https://hogproxy.laerdal.com/ingest';

let consentedMode = false;
let signedInCaptured = false;

// Remove PostHog's persisted state left over from a previously consented session
const clearPosthogStorage = () => {
  const token = process.env.REACT_APP_POSTHOG_DBU as string;
  if (!token) return;

  const cookieName = `ph_${token}_posthog`;
  const expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
  document.cookie = `${cookieName}=; ${expired}`;
  // posthog defaults to a cross-subdomain cookie, so also clear on the root domain
  const rootDomain = window.location.hostname.split('.').slice(-2).join('.');
  document.cookie = `${cookieName}=; ${expired}; domain=.${rootDomain}`;

  try {
    Object.keys(window.localStorage)
      .filter(key => key.startsWith(`ph_${token}`))
      .forEach(key => window.localStorage.removeItem(key));
  } catch { /* storage unavailable */ }
};

/**
 * Initialize PostHog immediately so no early events are dropped.
 * Starts anonymous (memory persistence, IP-stripped proxy) unless the OneTrust
 * cookie shows a prior C0002 opt-in; the SDK signal later confirms or overrides.
 */
export const initAnalytics = () => {
  //@ts-ignore
  if (posthog.__loaded) return;

  consentedMode = isConsentKnown() ? hasAnalyticsConsent() : getConsentHintFromCookie() === true;

  if (!consentedMode) {
    clearPosthogStorage();
  }

  posthog.init(process.env.REACT_APP_POSTHOG_DBU as string, {
    api_host: consentedMode ? CONSENTED_HOST : ANONYMOUS_HOST,
    ui_host: 'https://eu.posthog.com',
    persistence: consentedMode ? 'localStorage+cookie' : 'memory',
    person_profiles: 'identified_only',
    disable_surveys: !consentedMode,
    // replay is enabled remotely in the PostHog project; keep it consent-gated here
    disable_session_recording: !consentedMode,
    capture_pageview: false, // router effect in App.tsx captures $pageview
  });
  console.log(`Posthog initialized (${consentedMode ? 'consented' : 'anonymous'} mode)`);
};

/** Sync PostHog with the current consent state and user. Safe to call repeatedly. */
export const applyConsentState = (user: User | undefined) => {
  try {
    // Until the OneTrust SDK reports, stick with the mode chosen at init
    const consented = isConsentKnown() ? hasAnalyticsConsent() : consentedMode;

    if (consented && !consentedMode) {
      consentedMode = true;
      posthog.set_config({
        persistence: 'localStorage+cookie',
        api_host: CONSENTED_HOST,
        disable_surveys: false,
        disable_session_recording: false,
      });
    } else if (!consented && consentedMode) {
      consentedMode = false;
      // reset while cookie persistence is active so the identified id is cleared from it
      posthog.reset();
      posthog.set_config({
        persistence: 'memory',
        api_host: ANONYMOUS_HOST,
        disable_surveys: true,
        disable_session_recording: true,
      });
      clearPosthogStorage();
    }

    if (consentedMode && user) {
      identifyUser(user);
    }
  } catch (error) {
    console.error('posthog config error', error);
  }
};

/** Clear the PostHog identity on logout; must run before the Auth0 redirect. */
export const resetAnalytics = () => {
  try {
    posthog.reset();
    signedInCaptured = false;
  } catch (error) {
    console.error('posthog reset error', error);
  }
};

// Identity payload is an allowlist: user/org ids, org name, role — never email or names
const identifyUser = (user: User) => {
  const isOrgOwner = user.currentOrganization?.members
    ?.find(member => member.user.id === user.id)
    ?.permissions?.some(permission => permission.permissionId === 'account.owner') ?? false;

  const newIdentification = posthog.get_distinct_id() !== user.id;

  posthog.identify(user.id, {
    org_role: isOrgOwner ? 'admin' : 'user',
    language: i18next?.language ?? 'en',
    user_profile: user.companyRoles?.map(role => role.name).join('/'),
    test_user: (user.email ?? '').indexOf('@laerdal.') > -1,
    org_id: user.currentOrganization?.id,
    org_name: user.currentOrganization?.name,
  });

  if (newIdentification && !signedInCaptured) {
    signedInCaptured = true;
    posthog.capture('User SignedIn');
  }

  if (user.currentOrganization) {
    let industryString = '';
    try {
      industryString = Industry[user.currentOrganization.industry];
    } catch {
      industryString = '';
    }

    posthog.group(
      'organisation',
      user.currentOrganization.customerNo ?? user.currentOrganization.id,
      {
        name: user.currentOrganization.name,
        orgId: user.currentOrganization.id,
        customerNumber: user.currentOrganization.customerNo,
        industry: industryString,
        country: user.currentOrganization.address?.country?.name,
      });
  }
};
